import * as React from "react";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { getValidityFromState } from "../../Util/Util/from-state.js";
import { focusToTopErrorFieldOf, focusToTopErrorFieldOnScreen } from "../../Util/Util/focus-to-top-error-field.js";
import { markFormFieldsTouched } from "../../Util/Util/mark-fields-touched.js";
const FormContext = React.createContext({});
FormContext.displayName = "FormContext";
const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error(
      "No FormContext available. You might still need to wrap the application with the FormProvider component."
    );
  }
  return context;
};
class Form extends React.Component {
  constructor(props) {
    super(props);
    this.handleState = (cb, formControlState) => {
      this.setState((prevState) => {
        const newFormState = {
          ...prevState.formState,
          [formControlState.name]: { ...formControlState }
        };
        return {
          formState: { ...newFormState },
          formValid: getValidityFromState(newFormState)
        };
      }, cb);
    };
    this.handleChange = () => {
      this.props.onChange && this.props.onChange(this.state);
    };
    this.handleBlur = () => {
      this.props.onBlur && this.props.onBlur(this.state);
    };
    this.handleSubmit = (e) => {
      const { onSubmit, showErrorsOnInvalidSubmit = true } = this.props;
      if (e) {
        e.preventDefault();
      }
      if (e?.target) {
        focusToTopErrorFieldOf(e.target);
      } else {
        focusToTopErrorFieldOnScreen();
      }
      if (showErrorsOnInvalidSubmit && !this.state.formValid) {
        return this.setState({
          formState: markFormFieldsTouched(this.state.formState)
        });
      }
      onSubmit && onSubmit(this.state);
    };
    this.onBlur = this.handleState.bind(this, this.handleBlur);
    this.onChange = this.handleState.bind(this, this.handleChange);
    this.onSubmit = this.handleState.bind(this, this.handleSubmit);
    this.getInitialState = (key) => this.state.formState[key];
    this.removeFieldState = (key) => {
      if (!this.state.formState[key]) {
        return;
      }
      const formState = { ...this.state.formState };
      delete formState[key];
      this.setState({ formState });
    };
    this.setFieldState = (formControlState) => {
      this.setState((prevState) => {
        const newFormState = {
          ...prevState.formState,
          [formControlState.name]: { ...formControlState }
        };
        return {
          formName: prevState.formName,
          formState: { ...newFormState },
          formValid: getValidityFromState(newFormState)
        };
      });
    };
    this.resetForm = () => {
      if (this.formRef.current) {
        this.formRef.current.reset();
      }
      this.setState({
        formName: this.props.name,
        formState: this.props.initialFormState || {},
        formValid: false
      });
    };
    this.formRef = React.createRef();
    this.state = {
      formName: props.name,
      formState: props.initialFormState || {},
      formValid: false
    };
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.initialFormState && !isEqual(nextProps.initialFormState, this.props.initialFormState) && !isEmpty(this.props.initialFormState)) {
      this.setState({
        formState: nextProps.initialFormState
      });
    }
  }
  render() {
    const {
      className = "form",
      name,
      "data-t": dataT,
      showRequiredAndOptionalLabels = true
    } = this.props;
    const context = {
      name,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onSubmit: this.onSubmit,
      getInitialState: this.getInitialState,
      removeFieldState: this.removeFieldState,
      setFieldState: this.setFieldState,
      resetForm: this.resetForm,
      showRequiredAndOptionalLabels
    };
    return /* @__PURE__ */ React.createElement(FormContext.Provider, { value: context }, /* @__PURE__ */ React.createElement(
      "form",
      {
        id: name,
        className,
        "data-t": dataT || name,
        "aria-label": this.props["aria-label"],
        onSubmit: this.handleSubmit,
        noValidate: true,
        ref: this.formRef
      },
      this.props.children
    ));
  }
}
export {
  FormContext,
  Form as default,
  useFormContext
};
//# sourceMappingURL=Form.js.map
